import React, { useState, useEffect, useRef } from 'react'
import { Outlet } from '@tanstack/react-router'

import IntentSelectorPage from './pages/IntentSelectorPage.jsx'
import DashboardLanding from './pages/DashboardLanding.jsx'
import LandOwnerDashboard from './components/dashboards/LandOwnerDashboard.jsx'
import ComingSoonDashboard from './components/dashboards/ComingSoonDashboard.jsx'
import DashboardPersonaPicker from './components/dashboards/DashboardPersonaPicker.jsx'
import MapPage from './pages/MapPage.jsx'
import OpsConsolePage from './pages/OpsConsolePage.jsx'
import ProjectsPage from './pages/ProjectsPage.jsx'
import OrgAdminPage from './pages/admin/OrgAdminPage.jsx'
import PlatformAdminLayout from './platform/PlatformAdminLayout.jsx'
import TopNav from './components/TopNav.jsx'
// import SystemTimeline from './components/SystemTimeline.jsx'
import NotificationsDrawer from './components/NotificationsDrawer.jsx'
import UsersRolesPanel from './components/UsersRolesPanel.jsx'

import { useOperatingAoiInit } from './hooks/useOperatingAoiInit'
import { useRestrictedAutoEnableImagery } from './hooks/useRestrictedAutoEnableImagery'
import { useRestrictedPadDrivenImagery } from './hooks/useRestrictedPadDrivenImagery'
import { useRestrictedCaseSelection } from './hooks/useRestrictedCaseSelection'
import { useRestrictedInitialLayers } from './hooks/useRestrictedInitialLayers'
import { initUserBoundary } from './auth/userBoundary'
import { useImpersonationFromUrl } from './hooks/useImpersonationFromUrl'
import ImpersonationBanner from './components/dev/ImpersonationBanner.jsx'
import LicenseTermBanner from './components/LicenseTermBanner.jsx'
import ToastHost from './components/ToastHost.jsx'
import { useProjectStore } from './stores/useProjectStore'
import { useAuthStore, selectIsRestrictedOperator } from './stores/useAuthStore'
import { useAoiStore } from './stores/useAoiStore'
import { useSiteStore } from './stores/useSiteStore'
import { useAppNav } from './hooks/useAppNav'
import { useEffectiveOrg } from './hooks/useEffectiveOrg'
import { fetchWithAuth } from './api/fetchWithAuth'
import { useOrgName } from "./hooks/useOrgName";
import { isMapProductTab } from './constants/mapTabs'

import logo from './assets/dh_logo_tm_white.png'
import { UserCircle, ChevronDown, Bell, LayoutDashboard, Home, FolderOpen, MapPin, Shield, RefreshCcw } from 'lucide-react'
import { Navigate } from '@tanstack/react-router'

const AVAILABLE_DASHBOARDS = [
  { id: 'land_owner', label: 'Land Owners', description: 'Visibility and protection of surface assets', enabled: true },
  { id: 'land_management', label: 'Land Management', description: 'Lease compliance and close-out oversight', enabled: false },
  { id: 'engineering', label: 'Engineering', description: 'Design, change detection, and planning', enabled: false },
  { id: 'production', label: 'Production', description: 'Operational status and exceptions', enabled: false },
  { id: 'regulatory', label: 'Regulatory', description: 'Compliance, filings, and risk', enabled: false },
]

if (typeof window !== 'undefined') {
  window.__aoiStore = useAoiStore
  window.__projectStore = useProjectStore
}



// ── AppShell ───────────────────────────────────────────────────────────────
// Root route component. Always mounted. Renders persistent chrome + <Outlet />.
export function AppShell() {
  useOperatingAoiInit()
  useRestrictedCaseSelection()
  useRestrictedAutoEnableImagery()
  useRestrictedPadDrivenImagery()
  useRestrictedInitialLayers()
  useImpersonationFromUrl()

  // Wire the user-boundary auth subscription once, at runtime — see
  // initUserBoundary() for why this can't happen at module load (import cycle).
  useEffect(() => { initUserBoundary() }, [])

  const nav = useAppNav()
  const { isIntent, isDashboard, isMap, isPortal, isPadPlanner, isPlatformAdminRoute, activeTab, activeDash } = nav

  // Logged-in (or impersonated) org — drives the centered header logo.
  const org = useEffectiveOrg()

  // Which logo_url failed to load, not a boolean. A boolean would latch: once
  // one org's logo broke, every subsequent org would render as failed until
  // remount. Keying on the URL means a new org — or the same org re-presigned
  // on the next /orgs/me — retries on its own, with no effect to reset it.
  //
  // WHY THIS EXISTS: logo_url is a presigned S3 URL, and presigning is a local
  // signature — it succeeds even when the credentials are dead, so the server
  // sees nothing to log. The failure lands in the browser as a 403. Without an
  // onError the broken <img> rendered its alt text, which is the org name, and
  // that is byte-identical to the deliberate text fallback below. A credential
  // outage across both S3 buckets therefore presented as "this org has no
  // logo" and stayed that way until someone went looking.
  const [failedLogoUrl, setFailedLogoUrl] = useState(null)
  const logoBroken = !!org?.logo_url && failedLogoUrl === org.logo_url

  const loadProjects = useProjectStore((s) => s.loadProjects)
  const isGlobal = useProjectStore((s) => s.isGlobal)

  // Day 9 Day 2 — fetch the DB-backed profile so we can read
  // is_restricted_operator. Fire-and-forget on mount; the store
  // debounces concurrent calls and defaults to false (full shell)
  // until the response lands.
  const loadProfile = useAuthStore((s) => s.loadProfile)
  const authToken = useAuthStore((s) => s.token)
  const isRestricted = useAuthStore(selectIsRestrictedOperator)
  // Has /auth/me settled? Gate the modes strip on this so a restricted user
  // doesn't flash the full strip during the brief window where isRestricted
  // still reads its default false. On failure this is true with a null
  // profile, so we fall back to the non-restricted shell (modes shown).
  const profileLoaded = useAuthStore((s) => s.profileLoaded)
  // THE ORG NAME IS NOT IN THE HEADER ANY MORE. It sat between the bell and the
  // profile button and is gone; the `org?.name ?? null` that fed it went with
  // it, since nothing else read it.
  //
  // useOrgName() BELOW STAYS, and it is a different mechanism: it fetches
  // /api/v1/org into useAuthStore, which QcSummary, CollaborationRail and
  // QcFindingCard read. Removing it with the label would have taken the org
  // name out of three PadRekko and collaboration surfaces that have nothing to
  // do with this header.
  useEffect(() => {
    if (!authToken) return
    loadProfile()
  }, [authToken, loadProfile])

  // Day 9 Day 2 followup — Item #6's "operatingAois[0]" auto-select was
  // replaced by case-scoped selection in useRestrictedCaseSelection()
  // above. Operating AOIs now belong to PadRekko cases, not orgs, so
  // the AOI to activate is derived from the operator's current case,
  // not from a list-of-org-AOIs primitive.

  const [projectPromptDismissed, setProjectPromptDismissed] = useState(false)
  // Restricted operators see PadRekko only; the modes strip in TopNav is
  // unnecessary visual noise. Hide it. Wait for profileLoaded so we don't
  // render the strip on the default-false isRestricted and then yank it away.
  // /padplanner carries the strip too, so its own tab can show it is active.
  // The project prompt stays map-only: it is about the map's scope.
  const showModes = (isMap || isPadPlanner) && !isRestricted && profileLoaded;
  const showProjectPrompt = isMap && showModes && isGlobal && !projectPromptDismissed

  const [basin, setBasin] = useState('Permian')
  const [operator, setOperator] = useState('All Operators')
  const [assetType, setAssetType] = useState('Pads')
  const [notifications, setNotifications] = useState([])
  const [notifOpen, setNotifOpen] = useState(false)
  const [showUserPanel, setShowUserPanel] = useState(false)

  const unacknowledged = notifications.filter(
    (n) => ['medium', 'high', 'critical'].includes(n.severity) && !n.acknowledged_at
  )
  const unreadCount = unacknowledged.length
  const bellSeverity = getBellSeverity(notifications)

  // Platform admin — bypasses shell entirely, checked before hooks run would
  // violate rules-of-hooks so we check after all hooks, render a different tree.
  const token = window.__keycloak?.tokenParsed
  const isPlatformAdmin = token?.realm_access?.roles?.includes('platform_admin') === true

  const userInitials = (() => {
    const first = token?.given_name?.[0] ?? ''
    const last = token?.family_name?.[0] ?? ''
    if (first || last) return (first + last).toUpperCase()
    // fallback: first two chars of the username
    return (token?.preferred_username ?? 'U').slice(0, 2).toUpperCase()
  })()

  const userPanelRef = useRef(null)

  useEffect(() => {
    if (!showUserPanel) return
    function handleClickOutside(e) {
      if (userPanelRef.current && !userPanelRef.current.contains(e.target)) {
        setShowUserPanel(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showUserPanel])

  useEffect(() => {
    loadNotifications()
    const interval = setInterval(loadNotifications, 30_000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => { loadProjects() }, [loadProjects])

  useOrgName();

  useEffect(() => {
    if (!notifOpen) return
    const unread = notifications.filter((n) => !n.read_at)
    if (!unread.length) return
    const ids = unread.map((n) => n.id)
    fetchWithAuth('/api/v1/notifications/mark-read', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    }).catch((e) => { console.error("[ui/src/App.jsx] POST /api/v1/notifications/mark-read failed (swallowed):", e); })
    setNotifications((n) =>
      n.map((x) => (ids.includes(x.id) ? { ...x, read_at: new Date().toISOString() } : x))
    )
  }, [notifOpen])

  async function loadNotifications() {
    try {
      const res = await fetchWithAuth('/api/v1/notifications')
      if (!res.ok) throw new Error(`Failed to fetch notifications (${res.status})`)
      const data = await res.json()
      const rows = data.notifications ?? []
      setNotifications((prev) => {
        const byId = new Map(prev.map((n) => [n.id, n]))
        return rows.map((n) => {
          const existing = byId.get(n.id)
          return {
            ...existing, ...n,
            title: n.payload?.title || n.payload?.text || n.title || n.event_type || 'Untitled event',
            message: n.payload?.message || n.payload?.detail || n.message || 'No details found',
            action: n.payload?.action || null,
            acknowledged_at: n.acknowledged_at ?? existing?.acknowledged_at,
            read_at: n.read_at ?? existing?.read_at,
          }
        })
      })
    } catch (err) {
      console.error('Notification fetch failed:', err.message)
    }
  }

  async function acknowledgeNotification(notif) {
    const res = await fetchWithAuth(`/api/v1/notifications/${notif.id}/ack`, {
      method: 'POST',
    })
    if (!res.ok) {
      console.error('ACK failed:', res.status, await res.text())
      return
    }
    setNotifications((n) =>
      n.map((x) =>
        x.id === notif.id
          ? { ...x, acknowledged_at: new Date().toISOString(), read_at: new Date().toISOString() }
          : x
      )
    )
  }

  // Click handler for the AIPrime Map rail icon. Mirrors the TopNav handler
  // for the `explore` mode: switch to the explore map tab, and if SiteID is
  // currently enabled, turn it off (since explore is not the siteid mode).
  function goExploreFromRail() {
    if (useSiteStore.getState().siteIdEnabled) {
      useSiteStore.getState().toggleSiteId()
    }
    // Day 9 Day 2 — restricted operators never enter Explore. Bounce
    // their rail click to PadRekko (the only product they can access).
    nav.goMapTab(isRestricted ? 'padrekko' : 'explore')
  }

  // Platform admin UI takes over the full viewport (no AppShell chrome) when
  // both URL and role match. The role check matters because a non-admin who
  // guesses the URL falls through to the normal shell — Outlet then resolves
  // to PlatformAdminRoute, which has its own role guard that bounces them to /.
  if (isPlatformAdminRoute && isPlatformAdmin) {
    return (
      <div className="h-screen w-screen bg-bg text-gray-100 overflow-hidden flex flex-col">
        <ImpersonationBanner />
        <LicenseTermBanner />
        <ToastHost />
        <PlatformAdminLayout />
      </div>
    )
  }

  return (
    <div className="h-screen flex flex-col bg-bg text-gray-100">
      <ImpersonationBanner />
      <LicenseTermBanner />

      <header className="h-14 border-b border-slate-800 bg-bg-soft shadow-[0_1px_0_rgba(0,0,0,0.4)] shrink-0">
        <div className="relative h-full flex items-center justify-between px-4">

          <div className="flex items-center gap-3">
            <img
              src={logo}
              alt="DreamHammer Logo"
              className="h-8 w-auto object-contain opacity-95 select-none"
            />
          </div>

          {/* Middle section — dashboard persona picker still appears here when relevant.
              Home / Projects / Dashboards / AIPrime Map icons have moved to the LeftNavRail below. */}
          <div className="flex items-center gap-2">
            {isDashboard && (
              <DashboardPersonaPicker
                value={activeDash ?? ''}
                onChange={(value) => nav.setActiveDash(value || null)}
              />
            )}
          </div>

          {/* CENTER — logged-in (or impersonated) org logo, replacing the old
              hardcoded UL logo. Absolutely centered so it's independent of the
              left/right widths. Raw white <img> on the dark bar (no chip);
              text-falls back to the org name. pointer-events-none so it never
              blocks the persona picker beneath it on dashboards. */}
          {(org?.logo_url || org?.name) && (
            <div className="absolute left-1/2 -translate-x-1/2 flex items-center pointer-events-none">
              {org.logo_url && !logoBroken ? (
                <img
                  src={org.logo_url}
                  alt={org.name}
                  className="h-4 w-auto max-w-[200px] object-contain opacity-95 select-none"
                  // alt="" would be the accessible choice for a decorative
                  // logo, but it is deliberately the org name: this element is
                  // only reached when a logo is expected, and a screen reader
                  // should hear the org either way.
                  onError={() => {
                    console.error(
                      "[header] org logo failed to load — the presigned URL was rejected, " +
                      "which usually means the S3 credentials or object are gone, not that " +
                      "the org has no logo",
                      { org_id: org.id, org_name: org.name, logo_url: org.logo_url }
                    )
                    setFailedLogoUrl(org.logo_url)
                  }}
                />
              ) : (
                <span className="text-sm font-medium text-gray-200 max-w-[240px] truncate">
                  {org.name}
                </span>
              )}

              {/* The distinguishing mark. Shown ONLY when a logo was expected
                  and failed — never when the org simply has no logo, which is
                  an ordinary state and stays silent. Badge rather than a title
                  tooltip because the wrapper is pointer-events-none so it does
                  not block the persona picker beneath, and a tooltip nobody can
                  hover is not a signal. */}
              {logoBroken && (
                <span
                  className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium font-mono uppercase tracking-wide
                             text-amber-300 bg-amber-950/40 border border-amber-700/50 whitespace-nowrap"
                >
                  logo failed
                </span>
              )}
            </div>
          )}

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-xs">
              <span className="px-2 py-1 rounded-md bg-slate-900/60 border border-slate-700 text-gray-400">
                ENV: BETA - Pre-pilot
              </span>
              <span className="flex items-center gap-1 px-2 py-1 rounded-md bg-slate-900/60 border border-slate-700 text-gray-400">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />API
              </span>
            </div>
            <button
              onClick={() => setNotifOpen(true)}
              className={`relative flex items-center justify-center w-9 h-9 rounded-md border transition
                ${bellSeverity === 'critical' ? 'bg-red-500/20 border-red-500 text-red-300 animate-pulse'
                  : bellSeverity === 'medium' ? 'bg-yellow-500/20 border-yellow-500 text-yellow-300'
                    : bellSeverity === 'low' ? 'border-accent-action text-accent-action'
                      : 'bg-slate-900/70 border-slate-700 text-gray-300 hover:text-white hover:bg-slate-800'}`}
            >
              <Bell size={18} />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full
                  bg-accent-info text-[10px] text-black font-semibold flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setShowUserPanel((v) => !v)}
              className="flex items-center gap-1 px-2 py-1.5 rounded-md bg-slate-900/70
                border border-slate-700 text-gray-200 text-xs hover:bg-slate-800"
            >
              <UserCircle size={18} />
              <span className="font-medium">{userInitials}</span>
              <ChevronDown size={12} className="opacity-70" />
            </button>
          </div>
        </div>
      </header>
      <ToastHost />

      {/* 
      <SystemTimeline notifications={notifications} />
      */}

      <NotificationsDrawer
        open={notifOpen}
        onClose={() => setNotifOpen(false)}
        notifications={notifications}
        onAction={(notif) => {
          nav.dispatchNotificationNav(notif)
          if (notif.action?.type === 'map-focus') {
            useAoiStore.getState().selectAoi(notif.action.payload?.aoiId)
          }
          if (notif.action?.type === 'aoi-save') {
            useAoiStore.getState().saveAoi?.(notif.action.payload?.aoiId)
          }
          if (notif.action?.type === 'job-result') {
            const jobId = notif.action.payload?.jobId
            if (jobId) {
              // Dynamic import avoids circular deps if this module isn't already imported
              import('./stores/useAIOnDemandStore').then(({ useAIOnDemandStore }) => {
                useAIOnDemandStore.getState().selectJob(jobId)
                // Auto-show layer for completed jobs (the toggle is idempotent for non-completed)
                useAIOnDemandStore.getState().setJobLayerVisible(jobId, true)
              })
            }
          }
        }}
        onAcknowledge={acknowledgeNotification}
      />

      {showUserPanel && (
        <div
          ref={userPanelRef}
          className="absolute top-[60px] right-4 z-[200]"
          onClick={() => setShowUserPanel(false)}
        >
          <UsersRolesPanel />
        </div>
      )}

      {/* Body row: full-height rail | right column (strip + prompt + main). */}
      <div className="flex flex-1 overflow-hidden">
        <LeftNavRail
          isIntent={isIntent}
          isMap={isMap}
          isDashboard={isDashboard}
          isPlatformAdminRoute={isPlatformAdminRoute}
          isPlatformAdmin={isPlatformAdmin}
          activeTab={activeTab}
          onMap={goExploreFromRail}
          onHome={nav.goHome}
          onProjects={nav.goProjects}
          onDashboards={nav.openDashboardLanding}
          onPlatformAdmin={nav.goPlatformAdmin}
          isRestricted={isRestricted}
        />

        <div className="flex flex-1 overflow-hidden flex-col">
          {/* ── NO TOPNAV ON THE PORTAL, AND IT COSTS NOTHING TO REMOVE ─────
              showModes is `(isMap || isPadPlanner) && …`, so on a portal page EVERY control in
              TopNav's left half is already unrendered — the AOI and project
              pickers, the modes strip, the purchase strip. Search and the
              system-status pill next to them are commented out. What is left is
              one button: the collaboration rail toggle.

              AND THAT BUTTON IS INERT HERE, not merely uninformative.
              <CollaborationRail /> mounts in exactly one place —
              MapPage.jsx:606 — so pressing it on /portal flips
              useCollabRailStore and nothing listens. The portal's give and take
              happens in its own thread panel; PortalShell already records that
              portal notes write no collab_notifications row and that both
              notification endpoints are off the portal class's allowlist, so
              the unread badge beside it can only ever read zero.

              So this is 44px of chrome whose sole control does nothing on the
              route it is being drawn for. If the rail ever does serve the
              portal, this condition is the one line to revisit. */}
          {!isIntent && !isPortal && (
            <TopNav
              showModes={showModes}
              basin={basin} setBasin={setBasin}
              operator={operator} setOperator={setOperator}
              assetType={assetType} setAssetType={setAssetType}
            />
          )}

          {showProjectPrompt && (
            <div className="flex items-center justify-between px-4 py-2
              bg-yellow-500/10 border-b border-yellow-500/20 shrink-0">
              <div className="flex items-center gap-2">
                <FolderOpen size={13} className="text-yellow-400 shrink-0" />
                <span className="text-xs text-yellow-300">
                  No project selected — some features and imagery may be limited.
                  Select a project from the toolbar above.
                </span>
              </div>
              <button
                onClick={() => setProjectPromptDismissed(true)}
                className="text-[10px] text-yellow-500/60 hover:text-yellow-400 transition ml-4 shrink-0"
              >
                Dismiss
              </button>
            </div>
          )}

          <main className="flex-1 overflow-hidden">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  )
}

export default AppShell

// ── Route components ───────────────────────────────────────────────────────
// Thin wrappers used as `component:` in router.jsx.
// Kept here to avoid creating a file per route for trivial wrappers.

export function IntentRoute() {
  return <IntentSelectorPage />
}

export function DashboardRoute() {
  const nav = useAppNav()
  return (
    <DashboardLanding
      dashboards={AVAILABLE_DASHBOARDS}
      onSelect={(id) => {
        const d = AVAILABLE_DASHBOARDS.find((x) => x.id === id)
        nav.setActiveDash(d?.enabled ? id : 'coming_soon')
      }}
    />
  )
}

export function DashboardDetailRoute() {
  const nav = useAppNav()
  const { activeDash } = nav
  if (activeDash === 'land_owner') return <LandOwnerDashboard onOpenMap={() => nav.openMap()} />
  if (activeDash === 'coming_soon') return <ComingSoonDashboard onBack={() => nav.openDashboardLanding()} />
  return null
}

export function MapTabRoute() {
  const { activeTab } = useAppNav()

  const isMapProduct = isMapProductTab(activeTab)

  return (
    <>
      <div className={isMapProduct ? 'h-full w-full' : 'hidden'}>
        <MapPage />
      </div>
      {activeTab === 'ops' && <OpsConsolePage />}
      {activeTab === 'projects' && <ProjectsPage />}
      {activeTab === 'admin' && <OrgAdminPage />}
    </>
  )
}

export function PlatformAdminRoute() {
  // Unreachable for platform_admin users — AppShell intercepts the URL and
  // renders PlatformAdminLayout directly. This route fires for non-admins
  // who type the URL directly; bounce them to /.
  const token = window.__keycloak?.tokenParsed
  const isPlatformAdmin = token?.realm_access?.roles?.includes('platform_admin') === true
  if (!isPlatformAdmin) {
    return <Navigate to="/" />
  }
  return (
    <div className="h-screen w-screen bg-bg text-gray-100 overflow-hidden">
      <PlatformAdminLayout />
    </div>
  )
}

// ── Helpers ────────────────────────────────────────────────────────────────

function getBellSeverity(notifications) {
  const pending = notifications.filter((n) => !n.acknowledged_at)
  if (pending.some((n) => ['critical', 'high'].includes(n.severity))) return 'critical'
  if (pending.some((n) => n.severity === 'medium')) return 'medium'
  if (pending.length > 0) return 'low'
  return 'none'
}

function HeaderIconButton({ onClick, active, title, icon }) {
  return (
    <button
      onClick={onClick}
      title={title}
      aria-label={title}
      className={`relative flex items-center justify-center w-9 h-9 rounded-md border transition
        ${active
          ? 'border-accent-action text-accent-action'
          : 'bg-slate-900/70 border-slate-700 text-gray-300 hover:text-white hover:bg-slate-800'}`}
    >
      {icon}
    </button>
  )
}

// Slim vertical nav rail rendered to the left of <main>, below TopNav.
// Reuses HeaderIconButton for visual consistency with the (former) header icons.
function LeftNavRail({
  isIntent,
  isMap,
  isDashboard,
  isPlatformAdminRoute,
  isPlatformAdmin,
  activeTab,
  onMap,
  onHome,
  onProjects,
  onDashboards,
  onPlatformAdmin,
  isRestricted,
}) {
  // Day 9 Day 2 — restricted operator shell. Only the PadRekko entry
  // is rendered; everything else (Home / Explore / Projects / Platform
  // Admin) is hidden. Tanstack route guards still bounce them back to
  // /map/padrekko if they URL-jump elsewhere.
  if (isRestricted) {
    return (
      <nav
        className="w-12 shrink-0 border-r border-slate-800 bg-bg-soft
          flex flex-col items-center gap-2 py-3"
        aria-label="Primary"
      >
        <HeaderIconButton
          onClick={onMap}
          active={isMap && activeTab === 'padrekko'}
          title="PadRekko"
          icon={<RefreshCcw size={17} strokeWidth={1.8} />}
        />
      </nav>
    )
  }

  return (
    <nav
      className="w-12 shrink-0 border-r border-slate-800 bg-bg-soft
        flex flex-col items-center gap-2 py-3"
      aria-label="Primary"
    >

      <HeaderIconButton
        onClick={onHome}
        active={isIntent}
        title="Home"
        icon={<Home size={17} strokeWidth={1.8} />}
      />

      <HeaderIconButton
        onClick={onMap}
        active={isMap && activeTab === 'explore'}
        title="Explore"
        icon={<MapPin size={17} strokeWidth={1.8} />}
      />

      <HeaderIconButton
        onClick={onProjects}
        active={isMap && activeTab === 'projects'}
        title="Projects"
        icon={<FolderOpen size={17} strokeWidth={1.8} />}
      />

      {/*
      <HeaderIconButton
        onClick={onDashboards}
        active={isDashboard}
        title="Dashboards"
        icon={<LayoutDashboard size={17} strokeWidth={1.8} />}
      />
      */}

      {isPlatformAdmin && (
        <HeaderIconButton
          onClick={onPlatformAdmin}
          active={isPlatformAdminRoute}
          title="Platform Admin"
          icon={<Shield size={17} strokeWidth={1.8} />}
        />
      )}

    </nav>
  )
}
